import React, { useState } from 'react';
import { RowItem } from '../recs/engine';
import { UserProfile } from '../data/types';
import { fetchLlmExplanation } from '../api/justWatch';
import ContributionBarChart from './ContributionBarChart';
import CastCrew from './CastCrew';
import TitleDetails from './TitleDetails';
import './WhyRecommended.css';

interface Props {
  item: RowItem;
  viewer: UserProfile;
}

type ExplanationMode = 'exact' | 'ai';
type LlmState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; sentence: string }
  | { status: 'unavailable' };

export default function WhyRecommended({ item, viewer }: Props) {
  const { title, reason, bars, note } = item;
  // A `note` means this pick came from a fallback path (cold-start popularity, or a
  // rewatch once the demo's 30-title catalog is exhausted) — `bars` is present but
  // all zero, so the contribution chart and Exact/AI toggle have nothing real to show.
  const isModelBacked = Boolean(bars && bars.length > 0) && !note;

  const [mode, setMode] = useState<ExplanationMode>('exact');
  const [llmState, setLlmState] = useState<LlmState>({ status: 'idle' });

  function handleModeChange(next: ExplanationMode) {
    setMode(next);
    if (next === 'ai' && llmState.status === 'idle') {
      setLlmState({ status: 'loading' });
      fetchLlmExplanation(viewer.user_id, title.title_id)
        .then((res) => {
          setLlmState(res.available && res.sentence ? { status: 'ready', sentence: res.sentence } : { status: 'unavailable' });
        })
        .catch(() => setLlmState({ status: 'unavailable' }));
    }
  }

  return (
    <div className="detail-modal">
      {reason ? (
        <div className="detail-modal__why">
          <div className="detail-modal__why-header">
            <p className="detail-modal__why-label">Why recommended</p>
            {isModelBacked && (
              <div className="detail-modal__mode-toggle" role="tablist" aria-label="Explanation style">
                <button
                  role="tab"
                  aria-selected={mode === 'exact'}
                  className={'detail-modal__mode-btn' + (mode === 'exact' ? ' detail-modal__mode-btn--active' : '')}
                  onClick={() => handleModeChange('exact')}
                >
                  Exact
                </button>
                <button
                  role="tab"
                  aria-selected={mode === 'ai'}
                  className={'detail-modal__mode-btn' + (mode === 'ai' ? ' detail-modal__mode-btn--active' : '')}
                  onClick={() => handleModeChange('ai')}
                >
                  AI-phrased
                </button>
              </div>
            )}
          </div>

          {mode === 'exact' || !isModelBacked ? (
            <p className="detail-modal__why-text">{reason}</p>
          ) : llmState.status === 'loading' ? (
            <p className="detail-modal__why-text detail-modal__why-text--muted">Asking Claude…</p>
          ) : llmState.status === 'ready' ? (
            <p className="detail-modal__why-text">{llmState.sentence}</p>
          ) : (
            <p className="detail-modal__why-text detail-modal__why-text--muted">
              AI phrasing isn't available for this one right now — showing the exact breakdown instead.
            </p>
          )}

          {isModelBacked && bars && bars.length > 0 && (mode === 'exact' || llmState.status !== 'ready') && (
            <div className="detail-modal__chart">
              <ContributionBarChart bars={bars} />
            </div>
          )}
        </div>
      ) : (
        <div className="detail-modal__columns">
          <CastCrew titleId={title.title_id} />
          <TitleDetails title={title} />
        </div>
      )}
    </div>
  );
}
